import { QueryClient, useMutation } from "@tanstack/react-query";
import { V3UpdateCollection } from "../../../api/collections";
import { onCollectionOptimisticUpdate } from "./onCollectionOptimisticUpdate";
import { Queries, getAuthenticatedUserQuerie } from "../../../types/queries";
import { AxiosError } from "axios";
import { V3GenericErrorHandler } from "../../../api/error-handler";
import toast from "react-hot-toast";
import { NotificationTargets } from "../../../types/notification";
import { UserResponse } from "@authory-packages/core";
import { assign } from "lodash";

// Update collection mutation
export const getUpdateCollectionMutation = (queryClient: QueryClient, token: string) =>
    useMutation({
        mutationFn: V3UpdateCollection,
        onMutate: ({ params, useOptimistic = true, collectionId }) => {
            if (useOptimistic) return onCollectionOptimisticUpdate(token, queryClient, params, collectionId);
        },
        onError: (err: AxiosError, _, oldData) => {
            if (oldData) queryClient.setQueryData(getAuthenticatedUserQuerie(token), oldData);
            V3GenericErrorHandler(toast.error)(err);
        },
        throwOnError: false,
        onSuccess: (newData, { target, invalidateContent = false }) => {
            // Update user
            const oldUser = queryClient.getQueryData<UserResponse>(getAuthenticatedUserQuerie(token));

            const newUser = assign({}, oldUser, {
                collections: oldUser?.collections.map((coll) => {
                    let updatedNewData = { ...newData };

                    //@ts-ignore
                    if (coll?.ignoreCoverUpdated) {
                        updatedNewData = {
                            ...updatedNewData,
                            coverStyle: coll.coverStyle,
                            coverImage: coll.coverImage
                        };
                    }

                    return newData.canonicalSlug === coll.canonicalSlug ? assign({}, coll, updatedNewData) : coll;
                })
            });

            queryClient.setQueryData<UserResponse>(getAuthenticatedUserQuerie(token), newUser);
            queryClient.invalidateQueries({ queryKey: [Queries.Collections] });

            if (invalidateContent) {
                // Invalidate content queries relative to this collection only
                queryClient.invalidateQueries({
                    queryKey: [Queries.Content, token, newUser.slug.toLowerCase(), { collection: newData.canonicalSlug }]
                });
            }

            if (target === NotificationTargets.AddCollectionToProfile) {
                toast.success("Collection added");
            } else if (target === NotificationTargets.RemoveCollectionFromProfile) {
                toast.success("Collection removed");
            } else if (target === NotificationTargets.CollectionOrderProfile) {
                toast.success("Collection order updated");
            } else if (target === NotificationTargets.CollectionNameUpdated) {
                toast.success("Collection name updated");
            } else if (target === NotificationTargets.MarkAsFavorite) {
                toast.success("Collection has been favorited");
            } else if (target === NotificationTargets.RemoveFavorite) {
                toast.success("Collection is no longer favorited");
            } else if (target === NotificationTargets.SEO) {
                toast.success("Preview updated");
            } else if (target === NotificationTargets.CollectionInfoUpdated) {
                toast.success("Collection info updated");
            } else if (target === NotificationTargets.CollectionCoverUpdated) {
                toast.success("Cover updated");
            } else if (target === NotificationTargets.CollectionLayoutUpdated) {
                toast.success("Content layout updated");
            } else if (target === NotificationTargets.CollectionLinkSettingsUpdated) {
                toast.success("Link settings updated");
            } else if (target === NotificationTargets.CollectionShareUpdated) {
                toast.success("Collection share settings updated");
            }
        }
    });
